/**
 * EducationGuideModal — the Patient Education Guide, readable and printable
 * from inside the CRM (Card P77 item 10, 10 Oct 2026).
 *
 * WHAT WAS ACTUALLY MISSING
 *
 * The card asked for "staff can open the Education Guide PDF" and described a
 * presigned S3 GET. There is no PDF. Searched routes/, scripts/, migrations/,
 * src/ and all four pdf-lib call sites: nothing generates, stores or serves an
 * education-guide document, and no table holds one. The guide has only ever
 * existed as the public React page at /education.
 *
 * So there were three separate reasons staff could not view or download it,
 * and only one of them was about PDFs:
 *   1. no artifact existed to download;
 *   2. nothing in the CRM linked to the guide at all — DiscoveryCallPanel
 *      could SEND it to a patient but never show it to the person sending it;
 *   3. opening /education from the CRM is actively destructive: App.js
 *      matches that path before the auth check and renders the public page
 *      INSTEAD of the app shell, so the operator loses the CRM they were in.
 *
 * Daniel's ruling (10 Oct): render it in the CRM and print to PDF. No S3, no
 * stored artifact, no new PHI path — the content is generic and carries no
 * patient identifiers, so there is nothing here that needs a bucket.
 *
 * THE PRINT PATH
 *
 * Download = the browser's own print-to-PDF, driven by window.print(). The
 * print rules live in App.css under `@media print` (plain CSS, per the house
 * convention) and hide everything outside .education-guide-print-root: the
 * CRM sidebar, the modal chrome, the buttons and the backdrop. What reaches
 * the paper is the guide and nothing else.
 *
 * ⚠ THE PRINT ROOT IS A BODY-LEVEL CLASS, NOT A WRAPPER STYLE. Hiding the app
 * around a modal cannot be done from inside the modal: the sidebar is not its
 * ancestor. The class goes on <body> while the modal is open and comes off on
 * unmount — including the unmount that happens if the operator closes the tab
 * mid-print, which is why the cleanup is in the effect's return and not in
 * the close handler alone.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Globe, Printer } from 'lucide-react';
import { EDUCATION_GUIDE } from '../lib/educationGuideContent';
import EducationGuideBody from './EducationGuideBody';

export default function EducationGuideModal({ onClose, initialLang = 'en' }) {
  const [lang, setLang] = useState(initialLang === 'es' ? 'es' : 'en');
  const other = lang === 'en' ? 'es' : 'en';

  // Body class drives the @media print rules in App.css. Removed on unmount
  // so a stray class cannot leave the rest of the CRM unprintable.
  useEffect(() => {
    document.body.classList.add('education-guide-printing');
    return () => document.body.classList.remove('education-guide-printing');
  }, []);

  // Escape closes — matches the other CRM overlays.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * ⭐ RENDERED THROUGH A PORTAL TO <body> — review nit.
   *
   * The print rules pull `.education-guide-print-root` to the top-left with
   * `position: absolute`, which resolves against the nearest POSITIONED
   * ancestor. Rendered inline, that ancestor is whatever CRM container
   * happens to be `position: relative` around the contact record — so the
   * printed page could start wherever that container sits, which is how a
   * blank or half-empty first sheet appears.
   *
   * A portal to document.body makes the overlay a direct child of <body>,
   * so there is no positioned ancestor to anchor against and the print root
   * lands where the rules intend. It also matches what the body-scoped
   * selectors already assume.
   */
  const overlay = (
    <div
      data-testid="education-guide-modal"
      className="education-guide-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 1000, display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', padding: '2rem 1rem', overflowY: 'auto',
      }}
    >
      <div
        className="education-guide-print-root"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 760, width: '100%', background: '#fff',
          borderRadius: 14, padding: '1.5rem 1.75rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}
      >
        {/* Chrome — excluded from print by App.css */}
        <div
          className="education-guide-chrome"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, marginBottom: 16, flexWrap: 'wrap',
          }}
        >
          <div style={{
            fontSize: '0.7rem', fontWeight: 700, color: '#2563eb',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Patient Education Guide
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              data-testid="education-guide-lang-toggle"
              onClick={() => setLang(other)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE',
                borderRadius: 999, color: '#1E40AF', fontSize: '0.8rem',
                fontWeight: 700, cursor: 'pointer',
              }}
              aria-label={`Switch to ${EDUCATION_GUIDE[other].langLabel}`}
            >
              <Globe size={13} /> {EDUCATION_GUIDE[other].langLabel}
            </button>
            <button
              type="button"
              data-testid="education-guide-download"
              onClick={() => window.print()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', background: '#2E8B98', border: 'none',
                borderRadius: 6, color: 'white', fontSize: '0.8rem',
                fontWeight: 700, cursor: 'pointer',
              }}
              title="Opens your browser's print dialog — choose Save as PDF"
            >
              <Printer size={13} /> Download PDF
            </button>
            <button
              type="button"
              data-testid="education-guide-close"
              onClick={onClose}
              aria-label="Close"
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: 6, background: 'transparent', border: 'none',
                color: '#64748B', cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <EducationGuideBody lang={lang} />
      </div>
    </div>
  );

  // Guarded: jsdom and SSR both lack a document.body at module scope in some
  // harnesses, and a modal that throws is worse than one that renders inline.
  return (typeof document !== 'undefined' && document.body)
    ? createPortal(overlay, document.body)
    : overlay;
}
